import React, { useEffect } from 'react';

import { Tooltip } from '@mui/material';
import noop from 'lodash/noop';
import type {
    MRT_Cell,
    MRT_ColumnDef,
    MRT_DensityState,
    MRT_PaginationState,
    MRT_Row,
    MRT_RowData,
    MRT_SortingState,
    MRT_TableInstance,
    MRT_TableOptions,
} from 'material-react-table';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';

import { usePersistentState } from 'common/hooks';

import { useCurrentProduct } from 'api/products';
import { highlight } from 'licenses/LicenseList/highlightHwIdentifiers';

type MaterialTableProps<TData extends MRT_RowData> = {
    name: string;
    data: Array<TData>;
    columns: Array<MRT_ColumnDef<TData>>;
    definedColumnVisibility?: Array<any>;
    defaultSort?: Array<any>;
    rowCount?: number;
    onRowClicked?: (row: MRT_Row<TData>) => void;
    customActions?: React.ReactNode;
    isInitialLoading?: boolean;
    isServerFiltered?: boolean;
    onFilterChanged?: (filter: string) => void;
    onSortingChanged?: (sorting: MRT_SortingState) => void;
    pagination: MRT_PaginationState;
    setPagination: MRT_TableOptions<any>['onPaginationChange'];
    showProgressBars?: boolean;
    tableDensity?: MRT_DensityState;
    initialFilter?: string;
};

export function mapBoolean(value) {
    return value ? 'Yes' : 'No';
}

export function cellValue(row, entry) {
    if (typeof entry.accessor === 'function') {
        return entry.accessor(row);
    }
    if (typeof row[entry.accessor] === 'boolean') {
        return mapBoolean(row[entry.accessor]);
    }
    return row[entry.accessor];
}

export function CellWithEllipsis({ table, cell }: { table: MRT_TableInstance<any>; cell: MRT_Cell<any> }) {
    const globalFilter = table.getState()?.globalFilter;
    const highlighted = globalFilter ? highlight(table, cell.getValue(), globalFilter) : cell.getValue();
    const title = String(cell.getValue());
    return (
        <Tooltip title={title}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{highlighted}</span>
        </Tooltip>
    );
}

export function MaterialTable<TData extends MRT_RowData>({
    name,
    data,
    columns,
    definedColumnVisibility = [],
    defaultSort = [],
    rowCount,
    onRowClicked,
    customActions,
    isInitialLoading,
    isServerFiltered = false,
    onFilterChanged = noop,
    onSortingChanged = noop,
    pagination = { pageIndex: 0, pageSize: 15 },
    setPagination = noop,
    showProgressBars = false,
    tableDensity = 'compact',
    initialFilter = '',
}: MaterialTableProps<TData>) {
    const { data: currentProduct } = useCurrentProduct();

    const [globalFilter, setGlobalFilter] = usePersistentState(`${name}Filter`, initialFilter ?? '');
    const [sorting, setSorting] = usePersistentState<MRT_SortingState>(`${name}Sort`, defaultSort);
    const [persistedProductId, setPersistedProductId] = usePersistentState(`${name}Product`, currentProduct?.id);

    useEffect(() => {
        setPersistedProductId(currentProduct?.id);
    }, [currentProduct, setPersistedProductId]);

    useEffect(() => {
        if (initialFilter !== '') {
            setGlobalFilter(initialFilter);
        }
    }, [initialFilter, setGlobalFilter]);

    useEffect(() => {
        onFilterChanged(globalFilter);
    }, [onFilterChanged, globalFilter]);

    useEffect(() => {
        onSortingChanged(sorting);
    }, [onSortingChanged, sorting]);

    const [columnSizing, setColumnSizing] = usePersistentState(`${name}TableStateColumnSizing`, {}, false);
    const [columnOrder, setColumnOrder] = usePersistentState(
        `${name}TableStateColumnOrder`,
        columns.map(c => c.id ?? c.accessorKey),
        false
    );
    const [columnVisibility, setColumnVisibility] = usePersistentState(
        `${name}TableStateColumnVisibility`,
        Object.fromEntries(definedColumnVisibility.map(o => [o.id, o.isVisible])),
        false
    );

    const [density, setDensity] = usePersistentState<MRT_DensityState>(`${name}TableStateDensity`, tableDensity, false);

    const table = useMaterialReactTable({
        columns,
        data,
        layoutMode: 'grid',
        enableFullScreenToggle: false,
        enableColumnOrdering: true,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableColumnFilterModes: false,
        enableColumnFilters: false,
        enableStickyHeader: true,
        positionGlobalFilter: 'left',
        enableGlobalFilterModes: false,
        globalFilterFn: 'contains',
        manualFiltering: isServerFiltered,
        manualPagination: isServerFiltered,
        manualSorting: isServerFiltered,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnOrderChange: setColumnOrder,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnSizingChange: setColumnSizing,
        onDensityChange: setDensity,
        rowCount,
        defaultColumn: {
            sortDescFirst: false,
        },
        muiPaginationProps: {
            rowsPerPageOptions: [5, 15, 50, 100, 1000] as number[],
        },
        muiTableContainerProps: {
            sx: {
                height: '100%',
            },
        },
        muiTableHeadCellProps: {
            sx: {
                '& .Mui-TableHeadCell-ResizeHandle-Wrapper': {
                    marginRight: '-8px',
                    padding: '0',
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                position: 'relative',
                flexGrow: 1,
                flexShrink: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            },
        },
        muiTableBodyRowProps: ({ row }) => ({
            onClick: _ => onRowClicked(row),
            sx: {
                cursor: 'pointer',
            },
        }),
        muiTableBodyCellProps: ({ row }) => ({
            sx: {
                color: row.original.error ? 'red' : undefined,
                textDecoration: row.original.strikethrough ? 'line-through' : undefined,
            },
        }),
        renderTopToolbarCustomActions: _ => customActions,
        state: {
            isLoading: isInitialLoading,
            showProgressBars,
            globalFilter,
            pagination,
            sorting,
            columnSizing,
            columnOrder,
            columnVisibility,
            density,
        },
        initialState: {
            showGlobalFilter: true,
        },
    });

    useEffect(() => {
        table.setPageIndex(0);
    }, [globalFilter, table]);

    useEffect(() => {
        if (currentProduct && persistedProductId !== currentProduct.id) {
            table.setPageIndex(0);
            table.setGlobalFilter('');
            table.setSorting(defaultSort);
            table.setColumnVisibility({
                ...Object.fromEntries(table.getAllFlatColumns().map(o => [o.id, false])),
                ...Object.fromEntries(table.getVisibleFlatColumns().map(o => [o.id, true])),
                ...Object.fromEntries(definedColumnVisibility.map(o => [o.id, o.isVisible])),
            });
        }
    }, [currentProduct, defaultSort, definedColumnVisibility, persistedProductId, table]);

    return <MaterialReactTable table={table} />;
}
